import { z } from 'zod';

import { ProductReviewsResponseSchema } from './reviews.js';

// ---------------------------------------------------------------------------
// GET /v1/customer-reviews?locale=&limit=&offset=
//
// Public read facade over `customer_reviews` + `customer_review_photos`
// (RLS blocks anon; the api/ service-role client bypasses RLS, so the query
// itself MUST filter published=true AND wallpaper_id IS NOT NULL — spec
// visibility predicate). The linked wallpaper (slug + name) rides along so
// the gallery card can render the "хочу такие обои" CTA → PDP.
//
// Additive to the frozen /v1/ contract — new endpoints, no changes to
// existing routes. BIGINT ids → positive ints (NOT UUID).
// ---------------------------------------------------------------------------

export const CustomerReviewsGalleryRequestSchema = z.object({
  /** Optional BCP-47-ish locale filter (pl|en|de|cs). Omitted → all published. */
  locale: z.string().min(2).max(5).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(24),
  offset: z.coerce.number().int().min(0).default(0),
});
export type CustomerReviewsGalleryRequest = z.infer<typeof CustomerReviewsGalleryRequestSchema>;

export const CustomerReviewPhotoItemSchema = z.object({
  /** Bucket-relative path (wandero bucket) — for responsive URL building client-side. */
  storage_path: z.string(),
  /** Base public object URL (no imgproxy transform) — same contract as RealizationItem.image_url. */
  image_url: z.string().url(),
  sort_order: z.number().int(),
});
export type CustomerReviewPhotoItem = z.infer<typeof CustomerReviewPhotoItemSchema>;

export const CustomerReviewGalleryItemSchema = z.object({
  id: z.number().int().positive(),
  /** Always present in the gallery (predicate: wallpaper_id IS NOT NULL) → CTA always has a target. */
  wallpaper_id: z.number().int().positive(),
  author_name: z.string().nullable(),
  text: z.string().nullable(),
  rating: z.number().int().min(1).max(5).nullable(),
  locale: z.string(),
  sort_order: z.number().int(),
  /** 1—N photos, sorted by sort_order ASC. */
  photos: z.array(CustomerReviewPhotoItemSchema),
  /** Locale-resolved wallpaper URI/slug for the "хочу такие обои" CTA. */
  wallpaper_slug: z.string(),
  /** Locale-resolved wallpaper name (nullable — fallback to a generic label client-side). */
  wallpaper_name: z.string().nullable(),
});
export type CustomerReviewGalleryItem = z.infer<typeof CustomerReviewGalleryItemSchema>;

export const CustomerReviewsGalleryResponseSchema = z.object({
  items: z.array(CustomerReviewGalleryItemSchema),
  total: z.number().int().nonnegative(),
});
export type CustomerReviewsGalleryResponse = z.infer<typeof CustomerReviewsGalleryResponseSchema>;

// ---------------------------------------------------------------------------
// GET /v1/wallpapers/:id/customer-reviews-aggregate
//
// First-party aggregate for one wallpaper (published only; avg over non-null
// ratings + count). Reuses the frozen ProductReviewsResponse shape so the
// client SEO merge (mergeProductReviews, T5) can combine it with A-Manager
// reviews without a shape mismatch. EMPTY shape = { ratingValue: null,
// reviewCount: 0, reviews: [] }.
// ---------------------------------------------------------------------------

export const CustomerReviewsAggregateResponseSchema = ProductReviewsResponseSchema;
export type CustomerReviewsAggregateResponse = z.infer<
  typeof CustomerReviewsAggregateResponseSchema
>;
